import React, { useMemo, useCallback } from 'react';
import { Table } from 'ui/Table';
import { TextInput } from 'ui/TextInput';
import { DecimalInput } from 'ui/stateful/DecimalInput';
import Checkbox from 'ui/Checkbox';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { ESortOrder } from 'store/common/types';
import { ITableColumn, ITableRow } from 'ui/Table/types';
import { formatDateDisplay, formatPriceCents } from 'utils';
import { getCurrencySymbol } from 'utils/currency';
import { IAllocatableBooking, IBookingAllocationState, TAllocatableBookingSortKey } from '../types';
import { dollarsToCents, centsToDollars } from '../utils';

interface IAllocateToBookingsSectionProps {
  bookings: IAllocatableBooking[];
  allocations: IBookingAllocationState[];
  isLoading: boolean;
  isEnabled: boolean;
  isReadOnly?: boolean;
  sortBy: TAllocatableBookingSortKey;
  sortOrder: ESortOrder;
  onSort: (sortBy: TAllocatableBookingSortKey, sortOrder: ESortOrder) => void;
  onToggleBooking: (bookingUuid: string, booking: IAllocatableBooking) => void;
  onUpdateDescription: (bookingUuid: string, description: string) => void;
  onUpdateAmount: (bookingUuid: string, amountCents: number) => void;
  onSelectAll: () => void;
  exchangeCurrency: string;
  showOnlySelected: boolean;
  onShowOnlySelectedChange: (showOnlySelected: boolean) => void;
}

/**
 * Table column definitions for allocatable bookings.
 * Checkbox and Booking Ref are NOT sortable per spec.
 */
const getTableColumns = (selectAllCheckbox: React.ReactNode, isReadOnly: boolean): ITableColumn[] => [
  { id: 'checkbox', label: selectAllCheckbox, width: '50px', className: 'pl-2' },
  { id: 'bookingRef', label: 'Booking Ref', width: '180px' },
  { id: 'guestFullName', label: 'Guest', width: '150px', fieldToSortBy: isReadOnly ? undefined : 'guestFullName' },
  { id: 'arrivalDate', label: 'Arrival', width: '100px', fieldToSortBy: isReadOnly ? undefined : 'arrivalDate' },
  {
    id: 'totalSalesCost',
    label: 'Total Cost',
    width: '120px',
    fieldToSortBy: isReadOnly ? undefined : 'totalSalesCostCents',
  },
  {
    id: 'outstandingBalance',
    label: 'Outstanding',
    width: '120px',
    fieldToSortBy: isReadOnly ? undefined : 'outstandingBalanceCents',
  },
  { id: 'description', label: 'Description', width: '280px' },
  { id: 'toAllocate', label: isReadOnly ? 'Allocated' : 'To Allocate', width: '120px', className: 'text-right' },
];

// Max visible rows before scrolling kicks in
const MAX_VISIBLE_ROWS = 8;
const ROW_HEIGHT_PX = 44;
const HEADER_HEIGHT_PX = 40;

export const AllocateToBookingsSection: React.FC<IAllocateToBookingsSectionProps> = ({
  bookings,
  allocations,
  isLoading,
  isEnabled,
  isReadOnly = false,
  sortBy,
  sortOrder,
  onSort,
  onToggleBooking,
  onUpdateDescription,
  onUpdateAmount,
  onSelectAll,
  exchangeCurrency,
  showOnlySelected,
  onShowOnlySelectedChange,
}) => {
  // Calculate selection state for "select all" checkbox
  const selectedCount = useMemo(() => allocations.filter(a => a.isSelected).length, [allocations]);
  const isAllSelected = bookings.length > 0 && selectedCount === bookings.length;
  const isSomeSelected = selectedCount > 0 && selectedCount < bookings.length;

  // Create select all checkbox for header
  const selectAllCheckbox = useMemo(
    () => (
      <Checkbox
        checked={isAllSelected}
        indeterminate={isSomeSelected}
        onChange={onSelectAll}
        disabled={isReadOnly}
        color={isReadOnly ? 'gray-40' : undefined}
      />
    ),
    [isAllSelected, isSomeSelected, onSelectAll, isReadOnly]
  );

  const tableColumns = useMemo(() => getTableColumns(selectAllCheckbox, isReadOnly), [selectAllCheckbox, isReadOnly]);

  // Get allocation state for a booking
  const getAllocation = useCallback(
    (bookingUuid: string): IBookingAllocationState | undefined => {
      return allocations.find(a => a.bookingUuid === bookingUuid);
    },
    [allocations]
  );

  // Handle sort change
  const handleSort = useCallback(
    (newSortBy: string, newSortOrder: string) => {
      onSort(newSortBy as TAllocatableBookingSortKey, newSortOrder as ESortOrder);
    },
    [onSort]
  );

  // Handle amount blur from DecimalInput - receives number value
  const handleAmountBlur = useCallback(
    (bookingUuid: string, value: number | null) => {
      onUpdateAmount(bookingUuid, dollarsToCents(value));
    },
    [onUpdateAmount]
  );

  // Get currency symbol for prefix
  const currencySymbol = getCurrencySymbol(exchangeCurrency as any);

  // Filter bookings based on showOnlySelected (always filter when read-only)
  const filteredBookings = useMemo(() => {
    if (!showOnlySelected && !isReadOnly) return bookings;
    return bookings.filter(booking => {
      const allocation = getAllocation(booking.bookingUuid);
      return allocation?.isSelected ?? false;
    });
  }, [bookings, showOnlySelected, isReadOnly, getAllocation]);

  // Transform bookings to table rows
  const tableRows: ITableRow[] = useMemo(() => {
    return filteredBookings.map(booking => {
      const allocation = getAllocation(booking.bookingUuid);
      const isSelected = allocation?.isSelected ?? false;

      // Text color class for read-only mode
      const textColorClass = isReadOnly ? 'text-gray-100' : '';

      return {
        id: booking.bookingUuid,
        className: isSelected && !isReadOnly ? '!bg-green-25' : undefined,
        cells: [
          {
            id: 'checkbox',
            value: (
              <Checkbox
                checked={isSelected}
                onChange={() => onToggleBooking(booking.bookingUuid, booking)}
                disabled={isReadOnly}
                color={isReadOnly ? 'gray-40' : undefined}
              />
            ),
          },
          {
            id: 'bookingRef',
            value: (
              <a
                href={`/booking-manager/${booking.bookingUuid}/overview`}
                target="_blank"
                rel="noopener noreferrer"
                className={isReadOnly ? 'text-gray-100 underline' : 'text-black underline'}
              >
                {booking.humanReadableId}
              </a>
            ),
          },
          { id: 'guestFullName', value: <span className={textColorClass}>{booking.guestFullName}</span> },
          {
            id: 'arrivalDate',
            value: <span className={textColorClass}>{formatDateDisplay(booking.arrivalDate)}</span>,
          },
          {
            id: 'totalSalesCost',
            value: (
              <span className={textColorClass}>{`${currencySymbol} ${formatPriceCents(
                booking.totalSalesCostCents
              )}`}</span>
            ),
          },
          {
            id: 'outstandingBalance',
            value: (
              <span className={textColorClass}>{`${currencySymbol} ${formatPriceCents(
                booking.outstandingBalanceCents
              )}`}</span>
            ),
          },
          {
            id: 'description',
            value: isSelected ? (
              isReadOnly ? (
                <span className="text-15px text-gray-100">{allocation?.description || ''}</span>
              ) : (
                <TextInput
                  id={`description-${booking.bookingUuid}`}
                  value={allocation?.description ?? ''}
                  onChange={e => onUpdateDescription(booking.bookingUuid, e.currentTarget.value)}
                  placeholder=""
                  className="w-full"
                />
              )
            ) : null,
          },
          {
            id: 'toAllocate',
            value: isSelected ? (
              isReadOnly ? (
                <span className="text-15px text-gray-100 text-right block">
                  {currencySymbol} {formatPriceCents(allocation?.toAllocateCents ?? 0)}
                </span>
              ) : (
                <DecimalInput
                  value={centsToDollars(allocation?.toAllocateCents ?? 0)}
                  onBlur={val => handleAmountBlur(booking.bookingUuid, val)}
                  decimalPlaces={2}
                  className="min-w-0 flex-1 text-right outline-none pr-10px bg-transparent"
                  prefix={currencySymbol}
                  prefixClassName="text-gray-80 text-15px pl-10px"
                  wrapperClassName="border border-gray-40 h-35px w-full bg-white-hard"
                />
              )
            ) : null,
          },
        ],
      };
    });
  }, [
    filteredBookings,
    allocations,
    getAllocation,
    onToggleBooking,
    onUpdateDescription,
    handleAmountBlur,
    currencySymbol,
    isReadOnly,
  ]);

  // Calculate max height for scrollable area: header + (MAX_VISIBLE_ROWS * row height)
  const maxTableHeight = HEADER_HEIGHT_PX + MAX_VISIBLE_ROWS * ROW_HEIGHT_PX;

  return (
    <div className="font-pt-sans">
      <span className="block uppercase text-13px tracking-2xs text-black mb-10px">Allocate to Bookings</span>

      {!isEnabled ? (
        <span className="text-15px text-black italic">Travel Company required.</span>
      ) : isLoading ? (
        <LoadingBar label="Loading bookings..." />
      ) : (
        <>
          {/* Only show selected filter - hidden in read-only mode */}
          {!isReadOnly && (
            <label className="flex items-center gap-5px mb-10px cursor-pointer">
              <Checkbox
                checked={showOnlySelected}
                onChange={e => onShowOnlySelectedChange((e.target as HTMLInputElement).checked)}
              />
              <span className="text-15px text-black">Only show selected</span>
            </label>
          )}

          {/* Scrollable table container */}
          <div className="overflow-y-auto" style={{ maxHeight: `${maxTableHeight}px` }}>
            <Table
              columns={tableColumns}
              rows={tableRows}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              messageWhenEmpty="No bookings available for allocation"
              evenColor="white-hard"
              stickyHeader
            />
          </div>
        </>
      )}
    </div>
  );
};
